import { Search, Image as ImageIcon, Mic, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { parse, format, isToday, isYesterday } from 'date-fns';
import { Conversation } from '../../types';
import { getAvatarInitial, useClientAvatar } from '../../hooks/useClientAvatar.ts';

// Get display name with priority: apelido > chat_name > name
const normalizeName = (value?: string): string => {
  const cleaned = (value || '')
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned;
};

const getDisplayName = (conversation: Conversation): string => {
  const rawName = (conversation.apelido && conversation.apelido.trim() !== '')
    ? conversation.apelido
    : (conversation.chat_name || conversation.name || 'Cliente');
  const normalized = normalizeName(rawName);
  return normalized || 'Cliente';
};

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelectConversation: (conversation: Conversation) => void;
  onOpenNewMessageModal: () => void;
  className?: string;
}

const formatTime = (timeString?: string, dateString?: string): string => {
  if (!timeString) return '';

  let messageDate: Date | null = null;

  // If dateString is provided, parse it as dd/mm/yyyy and combine with time
  if (dateString) {
    const dateParts = dateString.split('/');
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // months are 0-based
      const year = parseInt(dateParts[2]);
      const timeFormats = ['hh:mm a', 'HH:mm', 'H:mm'];
      for (const f of timeFormats) {
        const parsedTime = parse(timeString, f, new Date());
        if (!isNaN(parsedTime.getTime())) {
          messageDate = new Date(year, month, day, parsedTime.getHours(), parsedTime.getMinutes());
          break;
        }
      }
    }
  }

  // If no dateString or parsing failed, try to parse as full datetime
  if (!messageDate) {
    const fullFormats = ["yyyy-MM-dd'T'HH:mm:ss.SSSxxx", "yyyy-MM-dd'T'HH:mm:ss"];
    for (const f of fullFormats) {
      const parsed = parse(timeString, f, new Date());
      if (!isNaN(parsed.getTime())) {
        messageDate = parsed;
        break;
      }
    }
  }

  // Fallback to parsing timeString as Date
  if (!messageDate) {
    messageDate = new Date(timeString);
  }

  if (!messageDate || isNaN(messageDate.getTime())) {
    return timeString;
  }

  if (isToday(messageDate)) {
    return format(messageDate, 'HH:mm');
  } else if (isYesterday(messageDate)) {
    return `ontem ${format(messageDate, 'HH:mm')}`;
  } else {
    // For list, if not today, show only date without time
    return format(messageDate, 'dd/MM/yyyy');
  }
};

// Function to truncate name with ellipsis and tooltip
const formatName = (name?: string): { displayName: string; fullName: string } => {
  const defaultName = 'Cliente';
  const fullName = name || defaultName;
  const maxLength = 18; // Maximum characters to display

  if (fullName.length <= maxLength) {
    return { displayName: fullName, fullName };
  }

  const truncated = fullName.substring(0, maxLength - 3) + '...';
  return { displayName: truncated, fullName };
};

const ConversationAvatar = ({ conversation }: { conversation: Conversation }) => {
  const displayName = getDisplayName(conversation);
  const { photo, handleImageError } = useClientAvatar({
    clienteId: conversation.id,
    photo: conversation.photo,
  });

  return (
    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
      {photo ? (
        <img
          src={photo}
          alt={displayName || 'Foto'}
          className="h-full w-full rounded-full object-cover"
          onError={handleImageError}
        />
      ) : (
        getAvatarInitial(displayName)
      )}
    </div>
  );
};

type LastMessageMediaType = 'audio' | 'image' | 'media';

const getLastMessageMediaType = (conversation: Conversation): LastMessageMediaType | null => {
  const rawType = String(conversation.lastMessageMediaType || '').toLowerCase();
  if (rawType.includes('audio')) return 'audio';
  if (rawType.includes('image') || rawType.includes('foto') || rawType.includes('photo') || rawType.includes('img')) {
    return 'image';
  }
  if (rawType) return 'media';

  if (conversation.lastMessageAudioUrl) return 'audio';
  if (conversation.lastMessageImageUrl) return 'image';
  if (conversation.lastMessageMediaUrl) return 'media';
  return null;
};

const getMediaLabel = (type: LastMessageMediaType | null): string => {
  if (type === 'audio') return '\u00C1udio';
  if (type === 'image') return 'Imagem';
  return 'M\u00EDdia';
};

const ConversationList = ({
  conversations,
  selectedId,
  onSelectConversation,
  onOpenNewMessageModal,
  className = '',
}: ConversationListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all' as 'all' | 'botOff');
  //const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  //const [editedName, setEditedName] = useState('');
  //const editContainerRef = useRef<HTMLDivElement>(null);
  const baseList = conversations;

  // Filtra pelas opções do filtro ativo
  const filteredByActive = baseList.filter((conversation) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'botOff') return conversation.botActive === false;
    return true;
  });

  // Busca por nome/telefone
  const filteredConversations = filteredByActive.filter(
    (conversation) =>
      (getDisplayName(conversation).toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      conversation.phoneNumber.includes(searchTerm)
  );

  return (
    <div className={`w-80 border-r border-gray-200 flex flex-col h-full ${className}`}>
      {/* Top: Botão Nova mensagem */}
      <div className="p-3 flex justify-between items-center border-b border-gray-200 bg-white">
        <span className="font-bold text-gray-700 text-lg">Conversas</span>
        {/*
        <button
          onClick={onOpenNewMessageModal}
          className="bg-[#2f7d72] text-white rounded-full p-2 hover:bg-[#27685f] transition"
          title="Nova mensagem"
        >
          <Plus size={20} />
        </button>
        */}
      </div>

      {/* Busca */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar conversa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Filtros de conversa */}
      <div className="flex gap-2 p-3 border-b border-gray-100 bg-gray-50">
        <button
          className={`px-3 py-1 rounded text-sm font-medium border transition
            ${activeFilter === 'all' ? 'bg-[#2f7d72] text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}
          `}
          onClick={() => setActiveFilter('all')}
        >
          Tudo
        </button>

        <button
          className={`px-3 py-1 rounded text-sm font-medium border transition
            ${activeFilter === 'botOff' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}
          `}
          onClick={() => setActiveFilter('botOff')}
        >
          IA desligada
        </button>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            Nenhuma conversa encontrada.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredConversations.map((conversation) => {
              const lastMessageText = (conversation.lastMessage || '').trim();
              const lastMessageMediaType = getLastMessageMediaType(conversation);
              const showMediaFallback = !lastMessageText && (
                lastMessageMediaType || conversation.lastMessageTime || conversation.lastMessageDate
              );
              const mediaLabel = getMediaLabel(lastMessageMediaType);
              const MediaIcon = lastMessageMediaType === 'audio'
                ? Mic
                : lastMessageMediaType === 'image'
                ? ImageIcon
                : Paperclip;
              const unreadCount = conversation.botActive ? 0 : (conversation.unreadCount ?? (conversation.unseen ? 1 : 0));
              const unreadLabel = `${unreadCount} ${unreadCount === 1 ? 'mensagem nova' : 'mensagens novas'}`;

              return (
                <div
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedId === conversation.id ? 'bg-teal-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ConversationAvatar conversation={conversation} />
                      <div className="ml-3">
                        <p className="text-sm flex items-center text-gray-900">
                          
                            <>
                              <span
                                title={formatName(getDisplayName(conversation)).fullName}
                                className="truncate max-w-[100px] inline-block"
                              >
                                {formatName(getDisplayName(conversation)).displayName}
                              </span>
                              
                            </>
                          <span
                            className={`ml-2 h-2 w-2 shrink-0 rounded-full ${
                              conversation.botActive ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          ></span>
                          {unreadCount > 0 && (
                            <span
                              className="ml-2 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 px-1 text-xs font-bold text-white"
                              aria-label={unreadLabel}
                              title={unreadLabel}
                            >
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{conversation.phoneNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatTime(conversation.lastMessageTime, conversation.lastMessageDate)}</span>
                    </div>
                  </div>

                  <p
                    className={`mt-1 text-xs truncate ${
                      unreadCount > 0 ? 'font-bold text-black' : 'text-gray-500'
                    }`}
                  >
                    {lastMessageText ? (
                      lastMessageText
                    ) : showMediaFallback ? (
                      <span className="inline-flex items-center gap-1.5">
                        <MediaIcon size={12} aria-hidden="true" />
                        {mediaLabel}
                      </span>
                    ) : (
                      <span className="text-gray-400">Sem mensagem</span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
